import { Alert, Anchor, List, Stack, Text } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';

import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { useCmsImage, useCmsSlot } from '../lib/cms';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});

function PrivacyPage() {
  // Every section on this page is CMS-editable: hero, draft banner, each
  // section's eyebrow / title / prose, and the closing "wider frame" tiles.
  // The bodies of "What we collect" (step_list) and "What we don't do"
  // (bullet_list) come from the base registry; the rest live in
  // packages/shared/src/cms/slots-extra/privacy.ts. The mailto anchors and the
  // bold legal-entity name stay as fixed JSX — the editable prose is split into
  // pre/post text runs around them, so output stays byte-identical.
  const heroEyebrow = useCmsSlot<'markdown'>('privacy.hero_eyebrow');
  const lastUpdated = useCmsSlot<'markdown'>('privacy.last_updated');
  const heroTitle = useCmsSlot<'markdown'>('privacy.hero_title');
  const heroLede = useCmsSlot<'markdown'>('privacy.hero_lede');

  const draftBannerTitle = useCmsSlot<'markdown'>('privacy.draft_banner_title');
  const draftBannerBody = useCmsSlot<'markdown'>('privacy.draft_banner_body');

  const whoWeAreEyebrow = useCmsSlot<'markdown'>('privacy.who_we_are_eyebrow');
  const whoWeAreTitle = useCmsSlot<'markdown'>('privacy.who_we_are_title');
  const whoWeArePre = useCmsSlot<'markdown'>('privacy.who_we_are_pre');
  const whoWeAreEntity = useCmsSlot<'markdown'>('privacy.who_we_are_entity');
  const whoWeArePost = useCmsSlot<'markdown'>('privacy.who_we_are_post');

  const whatWeCollectEyebrow = useCmsSlot<'markdown'>('privacy.what_we_collect_eyebrow');
  const whatWeCollectTitle = useCmsSlot<'markdown'>('privacy.what_we_collect_title');
  const whatWeCollect = useCmsSlot<'step_list'>('privacy.what_we_collect');

  const whatWeDontDoEyebrow = useCmsSlot<'markdown'>('privacy.what_we_dont_do_eyebrow');
  const whatWeDontDoTitle = useCmsSlot<'markdown'>('privacy.what_we_dont_do_title');
  const whatWeDontDo = useCmsSlot<'bullet_list'>('privacy.what_we_dont_do');

  const yourRightsEyebrow = useCmsSlot<'markdown'>('privacy.your_rights_eyebrow');
  const yourRightsTitle = useCmsSlot<'markdown'>('privacy.your_rights_title');
  const yourRightsPre = useCmsSlot<'markdown'>('privacy.your_rights_pre');
  const yourRightsPost = useCmsSlot<'markdown'>('privacy.your_rights_post');

  const cookiesEyebrow = useCmsSlot<'markdown'>('privacy.cookies_eyebrow');
  const cookiesTitle = useCmsSlot<'markdown'>('privacy.cookies_title');
  const cookiesBody = useCmsSlot<'markdown'>('privacy.cookies_body');

  const serviceProvidersEyebrow = useCmsSlot<'markdown'>('privacy.service_providers_eyebrow');
  const serviceProvidersTitle = useCmsSlot<'markdown'>('privacy.service_providers_title');
  const serviceProvidersIntro = useCmsSlot<'markdown'>('privacy.service_providers_intro');
  const serviceProviders = useCmsSlot<'step_list'>('privacy.service_providers');

  const changesEyebrow = useCmsSlot<'markdown'>('privacy.changes_eyebrow');
  const changesTitle = useCmsSlot<'markdown'>('privacy.changes_title');
  const changesBody = useCmsSlot<'markdown'>('privacy.changes_body');
  const changesContact = useCmsSlot<'markdown'>('privacy.changes_contact');

  const nextTitle = useCmsSlot<'markdown'>('privacy.next_title');

  const { image: heroImg, ready: heroImgReady } = useCmsImage('privacy.hero_image');

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={`${heroEyebrow} · ${lastUpdated}`}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.12}
      />

      <MarketingSection py="md">
        <Alert color="yellow" variant="light" title={draftBannerTitle}>
          {draftBannerBody}
        </Alert>
      </MarketingSection>

      <MarketingSection eyebrow={whoWeAreEyebrow} title={whoWeAreTitle}>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {whoWeArePre} <strong>{whoWeAreEntity}</strong> {whoWeArePost}{' '}
          <Anchor href="mailto:hello@wwaave.com" c="brand.7" fw={500}>
            hello@wwaave.com
          </Anchor>
          .
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={whatWeCollectEyebrow} title={whatWeCollectTitle} tinted>
        <List spacing="md" size="md" style={{ lineHeight: 1.65 }}>
          {whatWeCollect.map((item) => (
            <List.Item key={item.title}>
              <strong>{item.title}:</strong> {item.body}
            </List.Item>
          ))}
        </List>
      </MarketingSection>

      <MarketingSection eyebrow={whatWeDontDoEyebrow} title={whatWeDontDoTitle}>
        <List spacing="md" size="md" style={{ lineHeight: 1.65 }}>
          {whatWeDontDo.map((item) => (
            <List.Item key={item}>{item}</List.Item>
          ))}
        </List>
      </MarketingSection>

      <MarketingSection eyebrow={yourRightsEyebrow} title={yourRightsTitle} tinted>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {yourRightsPre}{' '}
          <Anchor href="mailto:hello@wwaave.com" c="brand.7" fw={500}>
            hello@wwaave.com
          </Anchor>{' '}
          {yourRightsPost}
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={cookiesEyebrow} title={cookiesTitle}>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {cookiesBody}
        </Text>
      </MarketingSection>

      <MarketingSection
        eyebrow={serviceProvidersEyebrow}
        title={serviceProvidersTitle}
        tinted
      >
        <Stack gap="md">
          <Text size="lg" style={{ lineHeight: 1.7 }}>
            {serviceProvidersIntro}
          </Text>
          <List spacing="md" size="md" style={{ lineHeight: 1.65 }}>
            {serviceProviders.map((item) => (
              <List.Item key={item.title}>
                <strong>{item.title}:</strong> {item.body}
              </List.Item>
            ))}
          </List>
        </Stack>
      </MarketingSection>

      {/* Untinted: the tinted/plain alternation runs the length of the page,
          and the service-providers section above took this one's turn. */}
      <MarketingSection eyebrow={changesEyebrow} title={changesTitle}>
        <Stack gap="md">
          <Text size="lg" style={{ lineHeight: 1.7 }}>
            {changesBody}
          </Text>
          <Text size="sm" c="ink.6">
            {changesContact}
          </Text>
        </Stack>
      </MarketingSection>

      <MarketingNextSteps
        title={nextTitle}
        destinations={['/terms', '/our-ethos']}
      />
    </PublicShell>
  );
}
